import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ChartPanelTabId } from 'views/chart/chart-panel-tabs/chartPanelTabsHelpers';

export type ElationAISurfaceDialogState = {
  isOpen: boolean;
  /**
   * When set, the popout/dialog is showing this clinical insights / Elation AI session.
   * Omitted or null when opening a shell that is not yet bound to a session.
   */
  displayedSessionId: string | null;
};

export type ElationAISliceState = {
  ui: {
    /** Which chart panel tab is currently active. `null` until initialized from the user's pinned preference. */
    activeChartPanelTab: ChartPanelTabId | null;
    surfaces: {
      dialog: ElationAISurfaceDialogState;
    };
  };
};

export const elationAIInitialState: ElationAISliceState = {
  ui: {
    activeChartPanelTab: null,
    surfaces: {
      dialog: {
        isOpen: false,
        displayedSessionId: null,
      },
    },
  },
};

export type OpenElationAIDialogPayload = {
  displayedSessionId?: string | null;
};

const elationAISlice = createSlice({
  name: 'elationAI',
  initialState: elationAIInitialState,
  reducers: {
    openElationAIDialog(state, action: PayloadAction<OpenElationAIDialogPayload | undefined>) {
      state.ui.surfaces.dialog = {
        isOpen: true,
        displayedSessionId: action.payload?.displayedSessionId ?? null,
      };
    },
    closeElationAIDialog(state) {
      state.ui.surfaces.dialog = {
        ...elationAIInitialState.ui.surfaces.dialog,
      };
    },
    /**
     * Resets all Elation AI surfaces (dialog, future middle chart / sidebar, etc.).
     * Use when the user should dismiss the feature UI (e.g. nav toggles off all surfaces).
     */
    closeAllElationAISurfaces(state) {
      state.ui = { ...elationAIInitialState.ui, activeChartPanelTab: state.ui.activeChartPanelTab };
    },
    setActiveChartPanelTab(state, action: PayloadAction<ChartPanelTabId>) {
      state.ui.activeChartPanelTab = action.payload;
    },
  },
});

const { actions, reducer: elationAIReducer } = elationAISlice;
const {
  openElationAIDialog,
  closeElationAIDialog,
  closeAllElationAISurfaces,
  setActiveChartPanelTab,
} = actions;

export {
  elationAIReducer,
  openElationAIDialog,
  closeElationAIDialog,
  closeAllElationAISurfaces,
  setActiveChartPanelTab,
};
